import { useDebounceValue } from '../../../../../../../../../logic/otherUtilities/useDebounceValue';
import { useMemo } from 'react';
import { ComboFormState, FormWordGroup } from '../models/ComboFormState';
import { FormikProps } from 'formik';

export interface CombinationWordCount {
  groups: FormWordGroup[];
  wordCount: number;
}

export interface KeywordCalculation {
  keywordCount: number;
  timeToExecuteMs: number;
  requestPermission: boolean;
}

export function countWords(value: string): number {
  return value.split(/[\n,]+/g).filter((s) => !!s.trim().length).length;
}

export function useCalculateKeywords(formState: FormikProps<ComboFormState>): KeywordCalculation {
  // This calculation can be intensive
  // So we debounce changes until no change has been made for 1000 ms, then calculate
  const debouncedValues = useDebounceValue(formState.values, 1000);
  return useMemo(() => {
    const selectedCombinations = debouncedValues.comboCombinations.filter(
      (cc) => cc.selected === true
    );
    const someHasWords = +debouncedValues.wordGroups.some((wg) => wg.words !== '');

    if (!selectedCombinations.length || !someHasWords) {
      return { keywordCount: 0, timeToExecuteMs: 0, requestPermission: false };
    }

    const wordCount = selectedCombinations
      .map((cc) =>
        cc.wordGroups
          .map((CCwg) => formState.values.wordGroups.find((wg) => wg.id === CCwg.id))
          .map((wordGroup) => countWords(wordGroup.words))
          .reduce((a, b) => a * b)
      )
      .reduce((a, b) => a + b);

    const requestCount = wordCount / 700;
    const maxDailyComboRequests = 6500;

    // We overesimtate the requests - it's more likely that it takes this time
    const overEstimateInRequests = requestCount * 1.15;

    // Calculate time
    const daysToExecuteMs =
      Math.floor(overEstimateInRequests / maxDailyComboRequests) * 24 * 3600 * 1000;
    const restExecuteMs = (overEstimateInRequests % maxDailyComboRequests) * 10 * 1000;

    return {
      keywordCount: wordCount,
      timeToExecuteMs: daysToExecuteMs + restExecuteMs,
      requestPermission: wordCount >= 4550000,
    };
  }, [debouncedValues]);
}
